import { Instance, Instances } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { AdditiveBlending, Fog, type Group, type Points } from 'three';
import { ICE_COLORS, ICE_LOOK, SIDE_COLORS } from './look.js';
import {
  BRAND_CAMP,
  layoutSurroundings,
  RIME_CAMP,
  SHORE_RADIUS,
  type Floe,
  type Pine,
} from './shore.js';

// Muted, so the board's ice stays the brightest thing on screen.
const SNOW = '#AEBDC7';
const SNOW_SHADE = '#97A8B3';
const ROCK = '#5E6870';
const PINE = '#1F3B34';
const WOOD = '#5B3F27';
const WOOD_DARK = '#3E2A1B';

/**
 * The scenery around the lake: open water, drifting floes, a snowy shore with banks, rocks and
 * pines, Brand's longship frozen in the ice behind rank 1, Rime's longhouse on the shore behind
 * rank 9, falling snow and a mist that only fades the distance. None of it is clickable.
 */
export function Surroundings({ seed, still }: { seed: number; still: boolean }) {
  const layout = useMemo(() => layoutSurroundings(seed), [seed]);
  return (
    <group>
      <DistanceMist />
      {/* The shore: snow from the water's edge outwards. */}
      <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <ringGeometry args={[SHORE_RADIUS, 40, 64, 1]} />
        <meshStandardMaterial color={SNOW} roughness={0.95} />
      </mesh>
      <Floes floes={layout.floes} still={still} />
      <Instances limit={layout.banks.length} range={layout.banks.length}>
        <sphereGeometry args={[1, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial roughness={0.95} />
        {layout.banks.map((bank, i) => (
          <Instance
            key={i}
            position={[bank.x, 0, bank.z]}
            scale={[bank.radius, bank.height, bank.radius * 0.8]}
            color={i % 3 === 0 ? SNOW_SHADE : SNOW}
          />
        ))}
      </Instances>
      <Instances limit={layout.rocks.length} range={layout.rocks.length}>
        <dodecahedronGeometry args={[1, 0]} />
        <meshStandardMaterial color={ROCK} roughness={0.9} flatShading />
        {layout.rocks.map((rock, i) => (
          <Instance
            key={i}
            position={[rock.x, rock.height * 0.35, rock.z]}
            scale={[rock.radius, rock.height, rock.radius]}
            rotation={[0, i, 0]}
          />
        ))}
      </Instances>
      <Pines pines={layout.pines} />
      <BrandCamp still={still} />
      <RimeCamp />
      {!still && <Snowfall />}
    </group>
  );
}

/** Fades only what is further than the board: the mist starts just past the far edge. */
function DistanceMist() {
  const scene = useThree((s) => s.scene);
  const fog = useMemo(() => new Fog(ICE_LOOK.background, 20, 40), []);
  useFrame(({ camera }) => {
    if (scene.fog !== fog) scene.fog = fog;
    const distance = camera.position.length();
    fog.near = distance + 6;
    fog.far = distance + 26;
  });
  return null;
}

/** Broken ice drifting on the open water, bobbing slowly out of step. */
function Floes({ floes, still }: { floes: readonly Floe[]; still: boolean }) {
  const refs = useRef<(Group | null)[]>([]);
  useFrame(({ clock }) => {
    if (still) return;
    floes.forEach((floe, i) => {
      const g = refs.current[i];
      if (!g) return;
      const t = clock.elapsedTime * 0.4 + floe.phase;
      g.position.y = 0.02 + Math.sin(t) * 0.015;
      g.rotation.y = floe.angle + Math.sin(t * 0.3) * 0.05;
    });
  });
  return (
    <Instances limit={floes.length} range={floes.length}>
      <boxGeometry args={[1, 0.1, 1]} />
      <meshStandardMaterial color={ICE_COLORS[0]} roughness={0.6} />
      {floes.map((floe, i) => (
        <Instance
          key={i}
          ref={(g: Group | null) => {
            refs.current[i] = g;
          }}
          position={[floe.x, 0.02, floe.z]}
          rotation={[0, floe.angle, 0]}
          scale={[floe.w, 1, floe.d]}
        />
      ))}
    </Instances>
  );
}

/** Snowy pines: three batched draws (trunks, branches, snow) for the whole forest. */
function Pines({ pines }: { pines: readonly Pine[] }) {
  const tiers = pines.flatMap((pine) =>
    [0, 1, 2].map((tier) => ({ pine, tier, y: pine.height * (0.35 + tier * 0.22) })),
  );
  return (
    <group>
      <Instances limit={pines.length} range={pines.length}>
        <cylinderGeometry args={[0.06, 0.08, 1, 6]} />
        <meshStandardMaterial color={WOOD_DARK} />
        {pines.map((pine, i) => (
          <Instance
            key={i}
            position={[pine.x, pine.height * 0.12, pine.z]}
            scale={[1, pine.height * 0.25, 1]}
          />
        ))}
      </Instances>
      <Instances limit={tiers.length} range={tiers.length}>
        <coneGeometry args={[1, 1, 7]} />
        <meshStandardMaterial color={PINE} roughness={0.9} flatShading />
        {tiers.map(({ pine, tier, y }, i) => (
          <Instance
            key={i}
            position={[pine.x, y, pine.z]}
            scale={[0.45 - tier * 0.11, pine.height * 0.42, 0.45 - tier * 0.11]}
          />
        ))}
      </Instances>
      <Instances limit={tiers.length} range={tiers.length}>
        <coneGeometry args={[1, 1, 7]} />
        <meshStandardMaterial color={SNOW} roughness={0.95} flatShading />
        {tiers.map(({ pine, tier, y }, i) => (
          <Instance
            key={i}
            position={[pine.x, y + pine.height * 0.1, pine.z]}
            scale={[0.3 - tier * 0.08, pine.height * 0.18, 0.3 - tier * 0.08]}
          />
        ))}
      </Instances>
    </group>
  );
}

/** Brand: a longship frozen into the lake, its orange sail, shields along the side, two fires. */
function BrandCamp({ still }: { still: boolean }) {
  const { body, trim } = SIDE_COLORS.brand;
  const shields = [-0.9, -0.45, 0, 0.45, 0.9];
  return (
    <group position={[BRAND_CAMP.x, 0, BRAND_CAMP.z]}>
      {/* The ice it is frozen in. */}
      <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0.2]}>
        <circleGeometry args={[1.7, 9]} />
        <meshStandardMaterial color={ICE_COLORS[2]} roughness={0.6} />
      </mesh>
      <group rotation={[0, 0.12, -0.05]}>
        {/* Hull, sunk to the waterline, with a high prow and stern. */}
        <mesh position={[0, 0.18, 0]}>
          <boxGeometry args={[2.8, 0.42, 0.85]} />
          <meshStandardMaterial color={WOOD} roughness={0.85} />
        </mesh>
        {[-1, 1].map((end) => (
          <group key={end} position={[end * 1.45, 0.3, 0]} rotation={[0, 0, end * -0.55]}>
            <mesh position={[0, 0.3, 0]}>
              <boxGeometry args={[0.16, 0.85, 0.22]} />
              <meshStandardMaterial color={WOOD} roughness={0.85} />
            </mesh>
            <mesh position={[end * 0.05, 0.75, 0]}>
              <sphereGeometry args={[0.1, 8, 6]} />
              <meshStandardMaterial color={WOOD_DARK} />
            </mesh>
          </group>
        ))}
        {/* Shields along both sides, in Brand's colours. */}
        {[-0.44, 0.44].map((side) =>
          shields.map((x, i) => (
            <mesh key={`${side}:${x}`} position={[x, 0.3, side]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.16, 0.16, 0.03, 14]} />
              <meshStandardMaterial color={i % 2 === 0 ? body : '#EFE6D6'} roughness={0.7} />
            </mesh>
          )),
        )}
        {/* Mast and sail, striped orange. */}
        <mesh position={[0, 1.1, 0]}>
          <cylinderGeometry args={[0.04, 0.05, 1.8, 6]} />
          <meshStandardMaterial color={WOOD_DARK} />
        </mesh>
        <mesh position={[0, 1.55, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 1.5, 6]} />
          <meshStandardMaterial color={WOOD_DARK} />
        </mesh>
        <group position={[0, 1.2, 0.05]} rotation={[0, Math.PI / 2, 0]}>
          {[0, 1, 2, 3, 4].map((stripe) => (
            <mesh key={stripe} position={[-0.56 + stripe * 0.28, 0, 0]}>
              <boxGeometry args={[0.28, 0.75, 0.02]} />
              <meshStandardMaterial color={stripe % 2 === 0 ? body : trim} roughness={0.8} />
            </mesh>
          ))}
        </group>
      </group>
      <Brazier x={-1.7} z={0.9} still={still} />
      <Brazier x={1.7} z={0.9} still={still} />
    </group>
  );
}

/** A fire in an iron bowl. Its glow on the ice is painted, not lit: real lights cost every surface. */
function Brazier({ x, z, still }: { x: number; z: number; still: boolean }) {
  const flame = useRef<Group>(null);
  const glow = useRef<Group>(null);
  const seed = x * 3.1;
  useFrame(({ clock }) => {
    if (still) return;
    const t = clock.elapsedTime * 9 + seed;
    const flicker = 1 + Math.sin(t) * 0.12 + Math.sin(t * 2.3) * 0.08;
    flame.current?.scale.set(1, flicker, 1);
    glow.current?.scale.set(flicker, 1, flicker);
  });
  return (
    <group position={[x, 0, z]}>
      <group ref={glow} position={[0, 0.04, 0]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.9, 24]} />
          <meshBasicMaterial
            color="#FF8A3D"
            transparent
            opacity={0.28}
            blending={AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      </group>
      <mesh position={[0, 0.25, 0]}>
        <cylinderGeometry args={[0.16, 0.08, 0.5, 8]} />
        <meshStandardMaterial color="#2E2A28" roughness={0.6} metalness={0.4} />
      </mesh>
      <group ref={flame} position={[0, 0.5, 0]}>
        <mesh position={[0, 0.14, 0]}>
          <coneGeometry args={[0.14, 0.32, 8]} />
          <meshBasicMaterial color="#FFB347" />
        </mesh>
        <mesh position={[0, 0.1, 0]}>
          <coneGeometry args={[0.09, 0.2, 8]} />
          <meshBasicMaterial color="#FFF1C2" />
        </mesh>
      </group>
    </group>
  );
}

/** Rime: a longhouse in the snow with a pitched, snowy roof, teal banners, shields and spears. */
function RimeCamp() {
  const { body, trim } = SIDE_COLORS.rime;
  return (
    <group position={[RIME_CAMP.x, 0, RIME_CAMP.z]}>
      <mesh position={[0, 0.45, 0]}>
        <boxGeometry args={[3, 0.9, 1.5]} />
        <meshStandardMaterial color={WOOD} roughness={0.85} />
      </mesh>
      {/* Pitched roof, under snow. */}
      {[-1, 1].map((side) => (
        <mesh key={side} position={[0, 1.12, side * 0.42]} rotation={[side * 0.62, 0, 0]}>
          <boxGeometry args={[3.3, 0.08, 1.05]} />
          <meshStandardMaterial color={SNOW} roughness={0.95} />
        </mesh>
      ))}
      <mesh position={[0, 0.32, 0.76]}>
        <boxGeometry args={[0.45, 0.65, 0.04]} />
        <meshStandardMaterial color={WOOD_DARK} />
      </mesh>
      {/* Banners on either side of the door. */}
      {[-1.9, 1.9].map((x) => (
        <group key={x} position={[x, 0, 0.9]}>
          <mesh position={[0, 1, 0]}>
            <cylinderGeometry args={[0.035, 0.04, 2, 6]} />
            <meshStandardMaterial color={WOOD_DARK} />
          </mesh>
          <mesh position={[0.22, 1.6, 0]}>
            <boxGeometry args={[0.42, 0.6, 0.02]} />
            <meshStandardMaterial color={body} roughness={0.8} />
          </mesh>
          <mesh position={[0.22, 1.6, 0.015]}>
            <boxGeometry args={[0.1, 0.6, 0.01]} />
            <meshStandardMaterial color={trim} roughness={0.8} />
          </mesh>
        </group>
      ))}
      {/* Shields leaning in the snow and spears stuck beside them, facing the lake. */}
      {[-1.1, -0.4, 0.4, 1.1].map((x, i) => (
        <group key={x} position={[x, 0, 1.35]}>
          <mesh position={[0, 0.2, 0]} rotation={[Math.PI / 2 - 0.35, 0, 0]}>
            <cylinderGeometry args={[0.2, 0.2, 0.03, 14]} />
            <meshStandardMaterial color={i % 2 === 0 ? body : trim} roughness={0.7} />
          </mesh>
          <mesh position={[0.28, 0.45, -0.05]} rotation={[0.1, 0, 0.08]}>
            <cylinderGeometry args={[0.015, 0.015, 0.9, 5]} />
            <meshStandardMaterial color={WOOD} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

const FLAKES = 450;

/** Gentle snowfall over and around the lake. */
function Snowfall() {
  const ref = useRef<Points>(null);
  const positions = useMemo(() => {
    const p = new Float32Array(FLAKES * 3);
    for (let i = 0; i < FLAKES; i++) {
      p[i * 3] = (Math.random() - 0.5) * 26;
      p[i * 3 + 1] = Math.random() * 9;
      p[i * 3 + 2] = (Math.random() - 0.5) * 26;
    }
    return p;
  }, []);
  useFrame((_, delta) => {
    const points = ref.current;
    if (!points) return;
    const attribute = points.geometry.getAttribute('position');
    const array = attribute.array as Float32Array;
    for (let i = 0; i < FLAKES; i++) {
      const y = i * 3 + 1;
      array[y] = (array[y] ?? 0) - delta * (0.35 + (i % 7) * 0.04);
      array[i * 3] = (array[i * 3] ?? 0) + Math.sin(((array[y] ?? 0) + i) * 0.7) * delta * 0.08;
      if ((array[y] ?? 0) < 0) array[y] = 9;
    }
    attribute.needsUpdate = true;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color="#FFFFFF" size={0.06} transparent opacity={0.8} depthWrite={false} />
    </points>
  );
}
